import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/layout/PublicNav";
import { BUSINESS_TYPES, STAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function Onboarding() {
  const { updateProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_type: "", stage: "", goals: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.business_type || !form.stage) { toast.error("Please select your business type and stage."); return; }
    setBusy(true);
    try { await updateProfile(form); toast.success("You're all set!"); navigate("/dashboard"); }
    catch { toast.error("Could not save profile."); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5 py-12 grain">
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8" data-testid="onboarding-form">
        <Logo />
        <h1 className="mt-8 font-heading text-3xl font-bold tracking-tight text-navy">Let's personalise your experience</h1>
        <p className="mt-2 text-slate-600">Tell us about your business so we can recommend the right resources and community spaces.</p>

        <div className="mt-8 space-y-6">
          <div>
            <Label>What kind of business do you run?</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="onboarding-business-type"><SelectValue placeholder="Select business type" /></SelectTrigger>
              <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>What stage are you at?</Label>
            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="onboarding-stage"><SelectValue placeholder="Select your stage" /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>What's your main goal right now?</Label>
            <Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="e.g. Get my first 100 customers" rows={3} data-testid="onboarding-goals" className="mt-1.5" />
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="onboarding-skip" className="text-slate-500">Skip for now</Button>
          <Button onClick={submit} disabled={busy} data-testid="onboarding-submit" className="flex-1 h-11 rounded-md bg-emerald font-semibold text-white hover:bg-emerald-700">{busy ? "Saving…" : "Continue to dashboard"}</Button>
        </div>
      </div>
    </div>
  );
}
